import React, { useState } from 'react';
import { 
  Sprout, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, HelpCircle, Trees
} from 'lucide-react';

interface AgriLandConsultantProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля аграрного та земельного аналізу...",
  "Експерт із земельного права аналізує цільове призначення та кадастровий статус...",
  "Податковий аналітик розраховує НГО, земельний податок та МПЗ за ст. 38-1 ПКУ...",
  "Земельний юрист оцінює ризики оренди та готує рекомендації...",
  "Формування підсумкового агро-земельного висновку..."
];

const LOADING_STEPS_EN = [
  "Initializing agricultural and land analysis AI agents...",
  "Land law expert checks cadastre status and usage rights...",
  "Tax analyst calculates NGO, land tax, and minimum tax liability (MPZ)...",
  "Legal consultant verifies lease contract risks...",
  "Generating final agricultural & land report..."
];

export default function AgriLandConsultant({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: AgriLandConsultantProps) {
  const isUk = language === 'uk';
  const [landType, setLandType] = useState(isUk ? 'Рілля / Сільгосппризначення (МПЗ)' : 'Arable / Agricultural Land (MPZ)');
  const [landDetailsText, setLandDetailsText] = useState(
    useEnterpriseContext ? (isUk ? 'Основна інформація про земельний банк компанії буде завантажена з бази знань.' : 'Company land bank details will be loaded from knowledge base.') : ''
  );
  const [calculationPreferencesText, setCalculationPreferencesText] = useState('');
  
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  const handleRun = async () => {
    if (!landDetailsText.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Аналіз земельних ділянок, розрахунок НГО та МПЗ",
          agentId: "agri-land-consultant",
          inputs: {
            land_type: landType,
            land_details_text: landDetailsText,
            calculation_preferences_text: calculationPreferencesText || (isUk ? "Стандартний розрахунок НГО та МПЗ" : "Standard NGO & MPZ calculation")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу земельних ділянок.' : 'Failed to analyze land plots.'));
      }

      const data = await response.json();
      
      // Handle async jobId polling if needed
      if (data.jobId) {
        const pollJob = async () => {
          const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
          if (!statusRes.ok) throw new Error(isUk ? 'Помилка перевірки статусу.' : 'Failed to check status.');
          const statusData = await statusRes.json();
          
          if (statusData.status === 'completed') {
            setReport(statusData.report);
            setIsAuditing(false);
          } else if (statusData.status === 'failed') {
            throw new Error(statusData.error || (isUk ? 'Аналіз завершився помилкою.' : 'Analysis failed.'));
          } else {
            setTimeout(pollJob, 3000);
          }
        };
        await pollJob();
      } else if (data.report) {
        setReport(data.report);
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = "Land_Agri_Report_" + landType.replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-emerald-600" />
              <span>{isUk ? "← До Агро-Центру" : "← Back to Agri Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sprout className="w-6 h-6 text-emerald-600" />
              <span>{isUk ? "Земельний & Агро Консультант" : "Agricultural & Land Consultant"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Аналіз земельних ділянок, кадастровий аудит, розрахунок НГО, МПЗ та оренди земли в Україні" : "Cadastre audits, NGO evaluation, MPZ tax calculations, and agricultural land lease checks"}
            </p>
          </div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
          <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Multi-Agent AI</span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-emerald-900">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "Земельний банк компанії" : "Company Land Bank")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 rounded text-emerald-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setLandType(isUk ? 'Рілля / Сільгосппризначення (МПЗ)' : 'Arable / Agricultural Land (MPZ)');
              setLandDetailsText(`Земельна ділянка 12.5 га (рілля), Кадастровий номер: 3222481600:01:001:0045, Бориспільський р-н, Київська обл.
НГО 1 га = 28 500 грн. Орендна плата 8% від НГО.
Потрібно розрахувати МПЗ (Мінімальне податкове зобов'язання за ст. 38-1 ПКУ) та земельний податок за 2025/2026 роки.`);
              setCalculationPreferencesText(`Розрахувати МПЗ з урахуванням сплачених податків (ЄП 4 група, ПДФО) та надати рекомендації щодо уникнення штрафних санкцій.`);
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Земельна ділянка & МПЗ)" : "⚡ Try 1-click Demo Sample (Land Plot & MPZ Tax)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Категорія земель / Мета аналізу" : "Land Category / Analysis Purpose"}
                </label>
                <select
                  value={landType}
                  onChange={(e) => setLandType(e.target.value)}
                  className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-emerald-500 focus:outline-none text-sm transition-colors text-slate-850"
                >
                  <option value={isUk ? "Рілля / Сільгосппризначення (МПЗ)" : "Arable / Agricultural Land (MPZ)"}>{isUk ? "Рілля / Сільгосппризначення (Розрахунок МПЗ)" : "Arable Land (MPZ tax calculation)"}</option>
                  <option value={isUk ? "Оренда земельних паїв (ФГ та Агрофірми)" : "Land Share Lease (Agri firms)"}>{isUk ? "Оренда земельних паїв (ФГ та Агрофірми)" : "Land Share Lease (Agri firms)"}</option>
                  <option value={isUk ? "Землі промисловості та корисних копалин" : "Industrial / Mining Land"}>{isUk ? "Землі промисловості та корисних копалин" : "Industrial / Mining Land"}</option>
                  <option value={isUk ? "Землі комунальної / державної власності" : "Municipal / State Owned Land"}>{isUk ? "Землі комунальної / державної власності" : "Municipal / State Owned Land"}</option>
                  <option value={isUk ? "Зміна цільового призначення ділянки" : "Land Usage Purpose Modification"}>{isUk ? "Зміна цільового призначення ділянки" : "Land Usage Purpose Modification"}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Деталі земельних ділянок / Кадастрові номери / Площа" : "Plot details / Cadastral numbers / Area"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Введіть кадастрові номери ділянок, площу (у га), регіон, актуальні ставки оренди чи опис поточних договорів..." 
                    : "Enter cadastral numbers, area in ha, region, current lease rates, or contract terms..."}
                  value={landDetailsText}
                  onChange={(e) => setLandDetailsText(e.target.value)}
                  rows={8}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                  {isUk ? "Побажання щодо розрахунку пільг / Оптимізації МПЗ (необов'язково)" : "Preferences on exemptions / MPZ optimization (optional)"}
                </label>
                <textarea
                  placeholder={isUk 
                    ? "Вкажіть пільги, розстрочки або специфіку вирощування сільгоспкультур для розрахунку МПЗ..." 
                    : "Specify exemptions, installments, or crop details for MPZ optimization..."}
                  value={calculationPreferencesText}
                  onChange={(e) => setCalculationPreferencesText(e.target.value)}
                  rows={4}
                  className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
                />
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!landDetailsText.trim()}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <Sprout className="w-4 h-4" />
              <span>{isUk ? "Провести агро-земельний аналіз" : "Run Agricultural & Land Audit"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-emerald-600" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-700 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "Експерт з кадастру перевіряє правовий статус та обмеження Земельного кодексу." : "Cadastre expert verifies legal status and Land Code constraints."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-700 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Податковий аналітик розраховує НГО, земельний податок та МПЗ за ст. 38-1 ПКУ." : "Tax analyst calculates NGO and Minimum Tax Liability (MPZ)."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-700 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Юрист оцінює ризики оренди та формує підсумковий звіт для агропідприємства." : "Lawyer reviews lease risks and builds final agricultural report."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-emerald-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-emerald-600 rounded-full animate-spin" />
            <Sprout className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Аналіз земельних ділянок аналітичними модулями..." : "Analyzing land plots with AI..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 p-1.5 rounded border border-emerald-100">
                <FileText className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Агро-земельний висновок" : "Agricultural & Land Report"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Document Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setLandDetailsText('');
                setCalculationPreferencesText('');
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Створити новий розрахунок" : "New Land Audit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
